import React from 'react'
import {connect} from 'react-redux'
import styled, {keyframes} from 'styled-components'
import {injectIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import {KeyboardArrowDown, Cached, Clear} from '@mui/icons-material'
import Typography from '@mui/material/Typography'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Popper from '@mui/material/Popper'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import MenuList from '@mui/material/MenuList'
import Popover from '@mui/material/Popover'
import Dialog from 'components/Dialog'
import {messageSet} from 'modules/messages/actions'
import {listFilter, getDefaultValue, isLinkedActive, isMobile, getAccess} from 'utils/utils'
import {linkedFieldNoFilterValue} from 'utils/helpers'
import {getLinkedOptions} from 'utils/utils'
import environment from 'utils/enums/environment'
import warning from 'utils/enums/messages'

const EXTRA_ROWS = 1
const ROW_HEIGHT = 36
const ROWS_COUNT = 5

const Field = styled(TextField)`
	width: 100%;
	margin-bottom: 20px;
`

const Delete = styled(Clear)`
	font-size: 15px;
	cursor: pointer;
	margin-left: 10px;
	pointer-events: auto;
`

const Placeholder = styled.span`
    color: #7c9198;
    font-style: italic;
    font-size: 13px;
    padding: 0 10px;
    width: 100%;
    height: 100%;
    line-height: inherit;
    cursor: pointer;
    overflow: hidden;
`

const Input = styled.input`
    font-size: 14px;
    padding: 0 10px;
    height: 100%;
    width: 100%;
`

const Value = styled(Link)`
    background-color: #cadce0;
    color: #4a4a4a;
    border-radius: 16px;
    max-width: 100%;
    padding: 0 12px;
    display: block;
    height: 32px;
    margin: 0 auto 0 10px;
    transition: background-color 250ms cubic-bezier(.55, 0, .1, 1), color 250ms cubic-bezier(.55, 0, .1, 1);
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    line-height: 32px;
`

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
`

const Empty = styled.div`
	padding: 14px 16px;
	font-size: 16px;
	text-align: center;
`

const LinkValue = styled(Value)`
	:hover {
		background-color: #415dd4;
		color: #fff;
	}
`

const Arrow = styled(KeyboardArrowDown)`
	padding: 0 10px;
	font-size: 22px;
	cursor: pointer;
	display: ${props => props.$loading ? 'none' : ''};
	transform: ${props => props.$open ? 'rotate(180deg)' : ''};
`

const rotate = keyframes`
	from {
		transform: rotate(0deg);
	}

	to {
		transform: rotate(360deg);
	}
`

const Loader = styled(Cached)`
	font-size: 22px;
	margin: 0 auto;
	animation: ${rotate} 1s linear infinite;
`

class LinkedField extends React.PureComponent {
    constructor(props) {
        super(props)

		this.mobile = isMobile()
		this.access = getAccess(props.foreignerEcn)
        this.options = []
        this.linkedOptions = []

        this.state = {
            open: null,
			errors: [],
            defaultValue: props.defaultValue,
            pattern: null,
            isLoading: false,
            start: 0,
            end: ROWS_COUNT
        }

    }

    componentDidUpdate(props) {
        if (props.defaultValue !== this.props.defaultValue) {
            this.setState({
                defaultValue: this.props.defaultValue
            })
        }
    }

    handleOpen = () => {
		const errors = this.props.environment ? isLinkedActive(this.props.name, this.props.row, this.props.environment) : []

		if (!errors.length) {
			if (!this.mobile) {
				const body = document.querySelector('body')
				body.className = 'noneEvents'
			}

			this.setState({
				isLoading: true
			}, () => this.getData())
		} else {
			this.setState({
				errors
			}, () => setTimeout(this.handleCloseError, 5000))
		}
    }

    getData = async () => {
        try {
            this.linkedOptions = await getLinkedOptions(this.props.name, this.props.row, this.props.environment)

            if (this.linkedOptions) {
                if (this.props.environment && !this.state.defaultValue && !this.props.linkedRelation) {
                    const value = getDefaultValue(this.props.environment, this.props.name, this.props.row)
                    if (value && value.length) {
                        const defaultValue = this.linkedOptions.find(item => item.id === value[0]) ?
                            this.linkedOptions.find(item => item.id === value[0]).name : ''

                        if (defaultValue) {
                            this.setState({
                                defaultValue
                            }, () => this.props.handleUpdateValue(value, this.props.name))
                        }
                    }
                }

                this.linkedOptions.forEach(item => this.options.push({...item}))

                this.setState({
                    open: true,
                    isLoading: false
                }, () => setTimeout(() => !this.state.pattern && this.input && this.input.select(), 5))
            } else {
                const body = document.querySelector('body')
                body.className = ''

                this.setState({
                    isLoading: false
                })
            }
        } catch (error) {
            const body = document.querySelector('body')
            body.className = ''

            this.setState({
                isLoading: false
            })

            this.props.messageSet(warning.linkedfieldOptionsError)
        }
    }

    handleClose = () => {
        const body = document.querySelector('body')
        body.className = ''

        this.options = []
        this.linkedOptions = []

        this.setState({
            open: false,
            pattern: null,
            start: 0,
            end: ROWS_COUNT
        })
    }

    handleClear = () => {
        this.setState({
            defaultValue: ''
        }, () => this.props.handleUpdateValue([], this.props.name))

        this.handleClose()
    }

    handleSelect = (e) => {
        let value, name
        const id = e.target.value
        if (id) {
            name = this.linkedOptions.find(item => item.id === id).name
            value = [id]
        } else {
            value = this.state.pattern
            name = this.state.pattern
        }

        this.setState({
            defaultValue: name
        }, () => this.props.handleUpdateValue(value, this.props.name))

        this.handleClose()
    }

    handleChangeValue = (e) => {
        this.options = listFilter(this.linkedOptions, e.target.value, ['name'])
        this.setState({
            pattern: e.target.value,
            start: 0,
            end: ROWS_COUNT + EXTRA_ROWS
        })
    }

    getListItem = (index, item) => {
        return <MenuItem
            onClick={this.handleSelect}
            key={item.id}
            value={item.id}
            selected={item.id === this.props.linkedRelation}
            style={{minHeight: ROW_HEIGHT + 'px'}}
        >
            <Typography variant='inherit' noWrap sx={{pointerEvents: 'none'}}>
                {item.name}
            </Typography>
        </MenuItem>
    }

    getList = () => {
        const list = []

        for (let i = this.state.start; i < this.state.end; i++) {
            if (!this.options[i]) break
            list.push(this.getListItem(i, this.options[i]))
        }

        return list
    }

    getVisible = (e) => {
        const container = document.getElementById('list')
        const scrollTop = container.scrollTop
        const currentIndex = Math.trunc(scrollTop / ROW_HEIGHT)
        const start = Math.max(currentIndex - EXTRA_ROWS, 0)
        const end = Math.min(currentIndex + ROWS_COUNT + EXTRA_ROWS, this.options.length)
        if (start !== this.state.start || end !== this.state.end) {
            this.setState({
                start,
                end
            })
        }
    }

	handleCloseError = () => {
		this.setState({
			errors: []
		})
	}

    render() {
        const {intl} = this.props
        const list = this.state.open && this.getList()
        const pattern = !!this.state.pattern && !!this.state.pattern.replace(/\s/g, '').length
		const readonly = (this.mobile && this.props.environment === environment.Collection) || this.props.readonly
		const editable = !this.props.disableCreate && pattern && this.access

        return (
            <Container ref={el => this.anchorEl = el}>
                {this.state.isLoading ?
                    <Loader />
                    :
                    <React.Fragment>
                        {this.state.open ?
                            <React.Fragment>
                                <Input
                                    defaultValue={this.state.defaultValue}
                                    ref={!this.mobile ? el => this.input = el : null}
                                    onChange={this.handleChangeValue}
                                />
                                {!this.mobile && this.state.defaultValue &&
                                    <Delete onClick={this.handleClear} />
                                }
                            </React.Fragment>
                            :
                            <React.Fragment>
                            {this.state.defaultValue && this.props.linkedRelation ?
                                <React.Fragment>
                                    {this.props.environment && this.access ?
										<LinkValue
											to={`/record/${this.props.foreignerEcn}/${this.props.linkedRelation}`}
										>
											{this.state.defaultValue}
										</LinkValue> :
                                        <Value as='div' to=''>
                                            {this.state.defaultValue}
                                        </Value>
                                    }
                                </React.Fragment>
                                :
                                <React.Fragment>
                                    {!readonly &&
                                        <Placeholder onClick={this.handleOpen}>
                                            {intl.formatMessage({id: "table.placeholder_linked"})}
                                        </Placeholder>
                                    }
                                </React.Fragment>
                            }
                            </React.Fragment>
                        }
                    </React.Fragment>
                }
                {!readonly &&
                    <Arrow
						$open={this.state.open}
						$loading={this.state.isLoading}
                        onClick={!this.state.open ? this.handleOpen : undefined}
                    />
                }
                {this.state.open &&
					<React.Fragment>
						{this.mobile ?
							<Dialog open fullWidth>
								<DialogContent>
									<Field
										variant="standard"
										defaultValue={this.state.defaultValue}
										onChange={this.handleChangeValue}
										inputRef={el => this.input = el}
										placeholder='Search item'

									/>
									<div>
										{editable &&
											<MenuItem
												disableGutters
												style={{color: '#415dd4'}} onClick={this.handleSelect}
											>
												+ Add {this.props.title}
											</MenuItem>
										}
										{this.options.map(item =>
											<MenuItem
												onClick={this.handleSelect}
												key={item.id}
												value={item.id}
												selected={item.id === this.props.linkedRelation}
											>
												{item.name}
											</MenuItem>
										)}
									</div>
								</DialogContent>
								<DialogActions>
									<Button onClick={this.handleClose} color="primary">
										{intl.formatMessage({id: "app.confirm_close"})}
									</Button>
									<Button onClick={this.handleClear} variant="contained" color="secondary">
										{intl.formatMessage({id: "app.clear"})}
									</Button>
								</DialogActions>
							</Dialog>
							:
							<Popper
								popperOptions={{
									positionFixed: true
								}}
								style={{
									zIndex: '1300'
								}}
								role={undefined}
								anchorEl={this.anchorEl}
								open={this.state.open}
							>
								<Paper id='list'
									onScroll={this.getVisible}
									elevation={8}
									style={{
										maxHeight: ROWS_COUNT * ROW_HEIGHT,
										margin: '5px 0',
										width: this.anchorEl.clientWidth + 'px',
										overflow: 'auto',
										pointerEvents: 'auto'
									}}
								>
									<ClickAwayListener onClickAway={this.handleClose}>
										{this.options.length || editable ?
											<MenuList style={{
												height: (this.options.length + (editable ? 1 : 0)) * ROW_HEIGHT
											}}>
												<MenuItem style={{height: this.state.start * ROW_HEIGHT, padding: 0}} />
												{editable &&
													<MenuItem style={{color: '#415dd4'}} onClick={this.handleSelect}>
														 <Typography variant='inherit' noWrap sx={{pointerEvents: 'none'}}>
														 	+ Add {this.props.title}
														 </Typography>
													</MenuItem>
												}
												{list}
											</MenuList>
											:
											<Empty>{intl.formatMessage({id: "app.not_found"})}</Empty>
										}
									</ClickAwayListener>
								</Paper>
							</Popper>
						}
					</React.Fragment>
                }
				{!!this.state.errors.length &&
					<Popover
						open={true}
						anchorEl={this.anchorEl}
						onClose={this.handleCloseError}
						PaperProps={{
							style: {
								margin: '5px 0',
                                width: this.anchorEl.clientWidth + 'px',
								color: '#fff',
								padding: '4px 8px',
								fontSize: '10px',
								lineHeight: '1.4em',
								borderRadius: '4px',
								backgroundColor: 'rgba(212, 91, 74, 0.9)'
							}
						}}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'center',
						}}
						transformOrigin={{
							vertical: 'top',
							horizontal: 'center',
						}}
					>
						{this.state.errors.map((item, index) =>
							<div key={index}>
								{intl.formatMessage({
									id: item.type === linkedFieldNoFilterValue ?
										"table.no_filter_value" : "table.used_by_other"
								}, {
									field: item.field.singular
								})}
							</div>
						)}
					</Popover>
				}
            </Container>
        )
    }
}

export default connect(null, {messageSet})(injectIntl(LinkedField))
